import { FC, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { Assessment, DomainComparisonEntry, OverallResults } from '@libs/types';
import { CaretDownIcon, CaretUpIcon, FileXIcon } from '@phosphor-icons/react';

import { DomainResultViewModel, OverallResultsViewModel } from '../../types';

import { DeltaTag } from './DeltaTag';
import { DomainResultItem } from './DomainResultItem';

const INITIAL_VISIBLE = 5;

type GapsFilter = 'all' | 'has_gaps' | 'missing_docs' | 'complete';

interface DetailedGapsTabProps {
  domainResults: DomainResultViewModel[];
  overallResults?: OverallResultsViewModel;
  comparisonOverall?: OverallResults;
  domainComparisonMap?: Map<string, DomainComparisonEntry>;
  activeVersion?: Assessment;
  baselineVersion?: Assessment;
  /** In-progress draft sibling — enables per-domain "Edit" buttons. */
  draftVersion?: Assessment;
  /** Called when a user clicks "Edit" but no draft exists yet. */
  onRequestNewVersion?: () => void;
}

export const DetailedGapsTab: FC<DetailedGapsTabProps> = observer(({
  domainResults,
  overallResults,
  comparisonOverall,
  domainComparisonMap,
  activeVersion,
  baselineVersion,
  draftVersion,
  onRequestNewVersion,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState<GapsFilter>('all');

  const isComparing = !!baselineVersion;

  // Compute all filter groups once to avoid duplicate passes
  const groups = useMemo(() => ({
    all: domainResults,
    has_gaps: domainResults.filter((d) => d.gaps > 0),
    missing_docs: domainResults.filter((d) => d.missingDocumentsCount > 0),
    complete: domainResults.filter(
      (d) => d.isApplicable && !d.isSkipped && d.gaps === 0 && d.missingDocumentsCount === 0,
    ),
  }), [domainResults]);

  const filtered = groups[filter];
  const hasMore = filtered.length > INITIAL_VISIBLE;
  const visible = showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);

  const filterOptions: { key: GapsFilter; label: string }[] = [
    { key: 'all',          label: t('assessments.overview.gapsFilter.all',         { count: groups.all.length }) },
    { key: 'has_gaps',     label: t('assessments.overview.gapsFilter.hasGaps',     { count: groups.has_gaps.length }) },
    { key: 'missing_docs', label: t('assessments.overview.gapsFilter.missingDocs', { count: groups.missing_docs.length }) },
    { key: 'complete',     label: t('assessments.overview.gapsFilter.complete',    { count: groups.complete.length }) },
  ];

  const totalMissing = overallResults?.missingDocuments ?? 0;
  const domainsWithMissingDocs = groups.missing_docs.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Missing documents summary bar */}
      {totalMissing > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            backgroundColor: `${theme.palette.warning.main}0A`,
            border: `1px solid ${theme.palette.warning.main}30`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileXIcon size={16} color={theme.palette.warning.dark} weight="fill" />
            <Typography variant="textSM_medium" sx={{ color: theme.palette.warning.dark }}>
              {t('assessments.overview.missingDocs.summary', {
                count: totalMissing,
                domains: domainsWithMissingDocs,
              })}
            </Typography>
          </Box>

          {isComparing && (
            <DeltaTag
              now={totalMissing}
              prev={comparisonOverall?.missingDocuments ?? 0}
              baselineLabel={baselineVersion ? `V${baselineVersion.version}` : ''}
              invertColor
            />
          )}
        </Box>
      )}

      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {filterOptions.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            size="small"
            onClick={() => { setFilter(key); setShowAll(false); }}
            sx={{
              fontSize: '0.75rem',
              fontWeight: filter === key ? 600 : 400,
              cursor: 'pointer',
              backgroundColor: filter === key ? theme.palette.primary.main : theme.palette.grey[100],
              color: filter === key ? 'white' : theme.palette.text.secondary,
              border: `1px solid ${filter === key ? theme.palette.primary.main : theme.palette.grey[300]}`,
              '&:hover': {
                backgroundColor: filter === key ? theme.palette.primary.dark : theme.palette.grey[200],
              },
            }}
          />
        ))}
      </Box>

      {/* Domain list */}
      {filtered.length === 0 ? (
        <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'text.tertiary' }}>
          <Typography variant="textSM_regular">{t('assessments.overview.gapsFilter.empty')}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {visible.map((domain, idx) => (
            <Box
              key={domain.domainId}
              sx={{
                borderTop: idx === 0 ? 'none' : `1px solid ${theme.palette.grey[200]}`,
                pt: idx === 0 ? 0 : 2,
              }}
            >
              <DomainResultItem
                domain={domain}
                compareEntry={domainComparisonMap?.get(domain.domainId)}
                activeVersion={activeVersion}
                baselineVersion={baselineVersion}
                draftVersion={draftVersion}
                onRequestNewVersion={onRequestNewVersion}
              />
            </Box>
          ))}
        </Box>
      )}

      {hasMore && (
        <Box sx={{ borderTop: `1px solid ${theme.palette.grey[200]}`, pt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="tertiary"
            size="small"
            onClick={() => setShowAll((v) => !v)}
            endIcon={showAll ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />}
          >
            {showAll
              ? t('assessments.overview.showLess')
              : t('assessments.overview.showAll', { count: filtered.length })}
          </Button>
        </Box>
      )}
    </Box>
  );
});
